/**
 * GoodGuardian — Violation Tracker
 *
 * Log and track court order violations. Each entry links to the specific
 * order and clause that was broken. One tap generates a Contempt Motion
 * via the Document Drafter.
 */
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { todayYMD, nowTimeStr } from '@/utils/dateUtils'

// ── Config ────────────────────────────────────────────────────────────────────
const VIOLATION_TYPES = [
  { value: 'withhold_visitation', label: '🚫 Withheld Visitation',       color: 'bg-red-100 text-red-700'      },
  { value: 'no_show',             label: '❌ No-Show',                    color: 'bg-red-100 text-red-700'      },
  { value: 'late_exchange',       label: '⏰ Late Exchange',              color: 'bg-orange-100 text-orange-700'},
  { value: 'communication',       label: '📵 Communication Blocked',      color: 'bg-amber-100 text-amber-700'  },
  { value: 'geographic',          label: '📍 Geographic Violation',       color: 'bg-purple-100 text-purple-700'},
  { value: 'financial',           label: '💰 Financial (support/expense)','color': 'bg-yellow-100 text-yellow-700'},
  { value: 'medical',             label: '🏥 Medical Decision',           color: 'bg-blue-100 text-blue-700'    },
  { value: 'other',               label: '📋 Other',                      color: 'bg-slate-100 text-slate-600'  },
]

const SEVERITY = [
  { value: 'low',      label: 'Low',      bg: 'bg-slate-100',   text: 'text-slate-600',   border: 'border-slate-300'   },
  { value: 'medium',   label: 'Medium',   bg: 'bg-amber-100',   text: 'text-amber-700',   border: 'border-amber-400'   },
  { value: 'high',     label: 'High',     bg: 'bg-orange-100',  text: 'text-orange-700',  border: 'border-orange-400'  },
  { value: 'critical', label: 'Critical', bg: 'bg-red-100',     text: 'text-red-700',     border: 'border-red-500'     },
]

const TYPE_MAP     = Object.fromEntries(VIOLATION_TYPES.map(t => [t.value, t]))
const SEVERITY_MAP = Object.fromEntries(SEVERITY.map(s => [s.value, s]))

const SEVERITY_BORDER = {
  low:      'border-l-slate-300',
  medium:   'border-l-amber-400',
  high:     'border-l-orange-500',
  critical: 'border-l-red-600',
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(ymd) {
  if (!ymd) return ''
  const [y, m, d] = ymd.split('-').map(Number)
  const dt    = new Date(y, m - 1, d)
  const today = todayYMD()
  if (ymd === today) return 'Today'
  const y2 = new Date(); y2.setDate(y2.getDate() - 1)
  const yest = y2.toISOString().split('T')[0]
  if (ymd === yest) return 'Yesterday'
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}

function buildContemptSummary({ violations, user }) {
  const lines = [
    `VIOLATION LOG — ${user?.name || 'Parent'} — ${user?.state || ''}`,
    `Generated: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`,
    `Total violations documented: ${violations.length}`,
    '',
  ]

  // Group by order
  const byOrder = {}
  for (const v of violations) {
    const key = v.order_id || '__none__'
    if (!byOrder[key]) byOrder[key] = { title: v.order_title || 'Unlinked order', items: [] }
    byOrder[key].items.push(v)
  }

  for (const { title, items } of Object.values(byOrder)) {
    lines.push(`COURT ORDER: ${title}`)
    lines.push(`${'─'.repeat(50)}`)
    for (const v of items) {
      const type = TYPE_MAP[v.type]?.label || v.type
      const sev  = SEVERITY_MAP[v.severity]?.label || v.severity
      lines.push(`• ${fmtDate(v.date)}${v.time ? ` at ${v.time}` : ''} — ${type} [${sev}]`)
      if (v.order_clause)   lines.push(`  Clause: ${v.order_clause}`)
      lines.push(`  ${v.description}`)
      if (v.witnesses)      lines.push(`  Witnesses: ${v.witnesses}`)
      if (v.evidence_notes) lines.push(`  Evidence: ${v.evidence_notes}`)
    }
    lines.push('')
  }

  lines.push('NOTE: Use this violation log as the factual basis for a Motion for Contempt.')
  lines.push('Include all exhibits referenced above. Attorney review required before filing.')

  return lines.join('\n')
}

// ── Add Violation Modal ───────────────────────────────────────────────────────
function AddViolationModal({ onClose, onSave, orders }) {
  const [form, setForm] = useState({
    date: todayYMD(), time: nowTimeStr(), type: '', severity: 'medium',
    order_id: '', order_clause: '', description: '', witnesses: '', evidence_notes: '',
  })
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState(null)
  const [formErrors, setFormErrors] = useState({})
  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setFormErrors(prev => ({ ...prev, [k]: '' })) }

  const selectedOrder = orders?.find(o => o.id === form.order_id)

  const handleSave = async () => {
    const errors = {}
    if (!form.type) errors.type = 'Violation type is required'
    if (!form.description.trim()) errors.description = 'What happened is required'
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return }
    setFormErrors({})
    setSaving(true)
    setError(null)
    try {
      await onSave({ ...form, time: form.time || null, order_id: form.order_id || null })
      onClose()
    } catch {
      setError('Could not save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={onClose}>
      <div
        className="w-full max-w-lg mx-auto bg-white dark:bg-slate-800 rounded-t-3xl p-5 flex flex-col gap-5 max-h-[93vh] overflow-y-auto no-scrollbar animate-slide-up"
        role="dialog" aria-modal="true" aria-label="Log violation"
        onClick={e => e.stopPropagation()}
      >
        <div className="w-12 h-1 rounded-full bg-slate-200 dark:bg-slate-600 mx-auto -mt-1" />
        <div>
          <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base">Log Violation</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Document exactly what happened. Be specific — this may become evidence.
          </p>
        </div>

        {/* Date + Time */}
        <div className="grid grid-cols-2 gap-3">
          <Input label="Date" type="date" value={form.date}
            onChange={e => set('date', e.target.value)} required />
          <Input label="Time (optional)" type="time" value={form.time}
            onChange={e => set('time', e.target.value)} />
        </div>

        {/* Violation type */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-2">
            Violation type <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {VIOLATION_TYPES.map(t => (
              <button key={t.value} onClick={() => set('type', t.value)}
                className={`text-left text-xs px-3 py-2.5 rounded-xl font-medium border-2 transition-all leading-snug ${
                  form.type === t.value
                    ? t.color + ' border-current'
                    : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'
                }`}>
                {t.label}
              </button>
            ))}
          </div>
          {formErrors.type && <p className="text-red-500 text-sm mt-1">{formErrors.type}</p>}
        </div>

        {/* Severity */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-2">Severity</label>
          <div className="flex gap-2">
            {SEVERITY.map(s => (
              <button key={s.value} onClick={() => set('severity', s.value)}
                className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all ${
                  form.severity === s.value
                    ? `${s.bg} ${s.text} ${s.border}`
                    : 'bg-white dark:bg-slate-700 text-slate-600 border-slate-200 dark:border-slate-600'
                }`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Court order link */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            Linked court order <span className="text-xs font-normal text-slate-400">(optional)</span>
          </label>
          {orders?.length > 0 ? (
            <select value={form.order_id} onChange={e => set('order_id', e.target.value)}
              className="input-base text-sm">
              <option value="">Select an order…</option>
              {orders.map(o => (
                <option key={o.id} value={o.id}>
                  {o.title}{o.date_issued ? ` — ${o.date_issued}` : ''}
                </option>
              ))}
            </select>
          ) : (
            <div className="bg-slate-50 dark:bg-slate-700 rounded-xl px-3 py-2.5 text-xs text-slate-500">
              No court orders on file.{' '}
              <span className="text-brand-600 font-semibold">Add orders first</span> to link violations.
            </div>
          )}
          {/* Show key terms hint when order selected */}
          {selectedOrder?.key_terms && (
            <div className="mt-2 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 rounded-xl px-3 py-2">
              <p className="text-xs font-semibold text-brand-700 dark:text-brand-300 mb-0.5">Order terms:</p>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed line-clamp-3">{selectedOrder.key_terms}</p>
            </div>
          )}
        </div>

        {/* Order clause */}
        <Input
          label="Specific clause or provision violated"
          value={form.order_clause}
          onChange={e => set('order_clause', e.target.value)}
          placeholder="e.g. Section 4.2 — weekly exchange at 3pm Fridays"
        />

        {/* Description */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            What happened <span className="text-red-500">*</span>
          </label>
          <textarea value={form.description} onChange={e => set('description', e.target.value)}
            rows={3} placeholder="Describe exactly what occurred, including times, locations, and what was said or done…"
            className="input-base resize-none text-sm" />
          {formErrors.description && <p className="text-red-500 text-sm mt-1">{formErrors.description}</p>}
        </div>

        {/* Witnesses */}
        <Input
          label="Witnesses"
          value={form.witnesses}
          onChange={e => set('witnesses', e.target.value)}
          placeholder="Names of anyone who witnessed this"
        />

        {/* Evidence notes */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            Evidence notes <span className="text-xs font-normal text-slate-400">(screenshots, photos, recordings)</span>
          </label>
          <textarea value={form.evidence_notes} onChange={e => set('evidence_notes', e.target.value)}
            rows={2} placeholder="Describe any evidence you have saved (text screenshots, call logs, etc.)"
            className="input-base resize-none text-sm" />
        </div>

        {error && <p className="text-xs text-red-600 font-medium">{error}</p>}

        <div className="flex gap-3 pb-1">
          <Button variant="ghost" size="md" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" className="flex-1"
            loading={saving} onClick={handleSave}>
            Save Violation
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Edit Violation Modal ──────────────────────────────────────────────────────
function EditViolationModal({ violation, onClose, onSave, orders }) {
  const [form, setForm] = useState({
    date:           violation.date           || todayYMD(),
    time:           violation.time           || '',
    type:           violation.type           || '',
    severity:       violation.severity       || 'medium',
    order_id:       violation.order_id       || '',
    order_clause:   violation.order_clause   || '',
    description:    violation.description    || '',
    witnesses:      violation.witnesses      || '',
    evidence_notes: violation.evidence_notes || '',
  })
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState(null)
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const selectedOrder = orders?.find(o => o.id === form.order_id)
  const canSave = form.date && form.type && form.description.trim()

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    setError(null)
    try {
      await onSave(violation.id, {
        ...form,
        time:     form.time     || null,
        order_id: form.order_id || null,
      })
      onClose()
    } catch {
      setError('Could not save changes. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={onClose}>
      <div
        className="w-full max-w-lg mx-auto bg-white dark:bg-slate-800 rounded-t-3xl p-5 flex flex-col gap-5 max-h-[93vh] overflow-y-auto no-scrollbar animate-slide-up"
        role="dialog" aria-modal="true" aria-label="Edit violation"
        onClick={e => e.stopPropagation()}
      >
        <div className="w-12 h-1 rounded-full bg-slate-200 dark:bg-slate-600 mx-auto -mt-1" />
        <div>
          <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base">Edit Violation</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Update the details of this violation record.
          </p>
        </div>

        {/* Date + Time */}
        <div className="grid grid-cols-2 gap-3">
          <Input label="Date" type="date" value={form.date}
            onChange={e => set('date', e.target.value)} required />
          <Input label="Time (optional)" type="time" value={form.time}
            onChange={e => set('time', e.target.value)} />
        </div>

        {/* Violation type */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-2">
            Violation type <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {VIOLATION_TYPES.map(t => (
              <button key={t.value} onClick={() => set('type', t.value)}
                className={`text-left text-xs px-3 py-2.5 rounded-xl font-medium border-2 transition-all leading-snug ${
                  form.type === t.value
                    ? t.color + ' border-current'
                    : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'
                }`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Severity */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-2">Severity</label>
          <div className="flex gap-2">
            {SEVERITY.map(s => (
              <button key={s.value} onClick={() => set('severity', s.value)}
                className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all ${
                  form.severity === s.value
                    ? `${s.bg} ${s.text} ${s.border}`
                    : 'bg-white dark:bg-slate-700 text-slate-600 border-slate-200 dark:border-slate-600'
                }`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Court order link */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            Linked court order <span className="text-xs font-normal text-slate-400">(optional)</span>
          </label>
          {orders?.length > 0 ? (
            <select value={form.order_id} onChange={e => set('order_id', e.target.value)}
              className="input-base text-sm">
              <option value="">Select an order…</option>
              {orders.map(o => (
                <option key={o.id} value={o.id}>
                  {o.title}{o.date_issued ? ` — ${o.date_issued}` : ''}
                </option>
              ))}
            </select>
          ) : (
            <div className="bg-slate-50 dark:bg-slate-700 rounded-xl px-3 py-2.5 text-xs text-slate-500">
              No court orders on file.
            </div>
          )}
          {selectedOrder?.key_terms && (
            <div className="mt-2 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 rounded-xl px-3 py-2">
              <p className="text-xs font-semibold text-brand-700 dark:text-brand-300 mb-0.5">Order terms:</p>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed line-clamp-3">{selectedOrder.key_terms}</p>
            </div>
          )}
        </div>

        {/* Order clause */}
        <Input
          label="Specific clause or provision violated"
          value={form.order_clause}
          onChange={e => set('order_clause', e.target.value)}
          placeholder="e.g. Section 4.2 — weekly exchange at 3pm Fridays"
        />

        {/* Description */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            What happened <span className="text-red-500">*</span>
          </label>
          <textarea value={form.description} onChange={e => set('description', e.target.value)}
            rows={3} placeholder="Describe exactly what occurred…"
            className="input-base resize-none text-sm" />
        </div>

        {/* Witnesses */}
        <Input
          label="Witnesses"
          value={form.witnesses}
          onChange={e => set('witnesses', e.target.value)}
          placeholder="Names of anyone who witnessed this"
        />

        {/* Evidence notes */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            Evidence notes <span className="text-xs font-normal text-slate-400">(screenshots, photos, recordings)</span>
          </label>
          <textarea value={form.evidence_notes} onChange={e => set('evidence_notes', e.target.value)}
            rows={2} placeholder="Describe any evidence you have saved"
            className="input-base resize-none text-sm" />
        </div>

        {error && <p className="text-xs text-red-600 font-medium">{error}</p>}

        <div className="flex gap-3 pb-1">
          <Button variant="ghost" size="md" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" className="flex-1"
            loading={saving} disabled={!canSave} onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Violation Card ────────────────────────────────────────────────────────────
function ViolationCard({ violation: v, onDelete, onEdit, onFileMotion }) {
  const type = TYPE_MAP[v.type]     || TYPE_MAP.other
  const sev  = SEVERITY_MAP[v.severity] || SEVERITY_MAP.medium
  const borderColor = SEVERITY_BORDER[v.severity] || 'border-l-slate-300'

  return (
    <div className={`card border-l-4 ${borderColor} flex flex-col gap-3 animate-slide-up`}>

      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${type.color}`}>
              {type.label}
            </span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sev.bg} ${sev.text}`}>
              {sev.label}
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
            {fmtDate(v.date)}{v.time ? ` at ${v.time}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onEdit(v)}
            className="tap-target text-slate-300 hover:text-brand-500 transition-colors"
            aria-label="Edit violation">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" />
            </svg>
          </button>
          <button onClick={() => onDelete(v.id)}
            className="tap-target text-slate-300 hover:text-red-400 transition-colors"
            aria-label="Delete violation">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Linked order */}
      {v.order_title && (
        <div className="flex items-start gap-1.5 bg-brand-50 dark:bg-brand-900/10 rounded-lg px-2.5 py-2">
          <span className="text-xs flex-shrink-0">⚖️</span>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-brand-700 dark:text-brand-300 truncate">{v.order_title}</p>
            {v.order_clause && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 italic">{v.order_clause}</p>
            )}
          </div>
        </div>
      )}

      {/* Description */}
      <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{v.description}</p>

      {/* Meta chips */}
      <div className="flex flex-wrap gap-1.5">
        {v.witnesses && (
          <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full">
            👁 {v.witnesses}
          </span>
        )}
        {v.evidence_notes && (
          <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full font-medium">
            📎 Evidence documented
          </span>
        )}
      </div>

      {/* File motion CTA */}
      <button
        onClick={() => onFileMotion(v)}
        className="w-full text-xs font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl py-2.5 hover:bg-red-100 transition-colors"
      >
        ⚖️ Use in Contempt Motion
      </button>
    </div>
  )
}

// ── Screen ────────────────────────────────────────────────────────────────────
export default function ViolationTracker() {
  const { violations, addViolation, updateViolation, deleteViolation, orders, user } = useApp()
  const navigate = useNavigate()

  const [showForm,    setShowForm]    = useState(false)
  const [editTarget,  setEditTarget]  = useState(null)   // violation being edited
  const [typeFilter,  setTypeFilter]  = useState('all')

  const filtered = useMemo(() =>
    typeFilter === 'all'
      ? violations
      : violations.filter(v => v.type === typeFilter),
    [violations, typeFilter]
  )

  // Group by date for the timeline
  const grouped = useMemo(() => {
    const map = {}
    for (const v of filtered) {
      if (!map[v.date]) map[v.date] = []
      map[v.date].push(v)
    }
    return Object.entries(map).sort(([a], [b]) => b.localeCompare(a))
  }, [filtered])

  const criticalCount = violations.filter(v => v.severity === 'critical' || v.severity === 'high').length
  const last30 = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30)
    const cutoffStr = cutoff.toISOString().split('T')[0]
    return violations.filter(v => v.date >= cutoffStr).length
  }, [violations])

  // Generate contempt motion with selected violations
  const handleFileMotion = (singleViolation) => {
    const list = singleViolation ? [singleViolation] : violations
    const context = buildContemptSummary({ violations: list, user })
    navigate('/documents', { state: { autoTemplate: 'contempt-motion', prefillContext: context } })
  }

  const handleFileAll = () => handleFileMotion(null)

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      <AppHeader title="Violation Tracker"
        right={
          <button onClick={() => setShowForm(true)}
            className="tap-target w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/80"
            aria-label="Log violation">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        }
      />

      <div className="screen no-scrollbar px-4 py-4 flex flex-col gap-4">

        {/* Stats */}
        {violations.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            <div className="card text-center py-3">
              <p className="text-2xl font-black text-slate-800 dark:text-slate-100">{violations.length}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
            <div className={`card text-center py-3 ${criticalCount > 0 ? 'border-red-200 dark:border-red-800' : ''}`}>
              <p className={`text-2xl font-black ${criticalCount > 0 ? 'text-red-600' : 'text-slate-800 dark:text-slate-100'}`}>
                {criticalCount}
              </p>
              <p className={`text-xs ${criticalCount > 0 ? 'text-red-500' : 'text-slate-500'}`}>
                High/Critical
              </p>
            </div>
            <div className="card text-center py-3">
              <p className="text-2xl font-black text-slate-800 dark:text-slate-100">{last30}</p>
              <p className="text-xs text-slate-500">Last 30 days</p>
            </div>
          </div>
        )}

        {/* Contempt Motion CTA */}
        {violations.length >= 1 && (
          <button
            onClick={handleFileAll}
            className="w-full flex items-center justify-between gap-3 bg-red-600 hover:bg-red-700 active:scale-[0.98] text-white rounded-2xl px-4 py-3.5 transition-all"
          >
            <div className="text-left">
              <p className="text-sm font-bold">⚖️ File Contempt Motion</p>
              <p className="text-xs text-red-200 mt-0.5">
                {violations.length} violation{violations.length > 1 ? 's' : ''} documented — generate court filing now
              </p>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Type filter */}
        {violations.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
            <button onClick={() => setTypeFilter('all')}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                typeFilter === 'all' ? 'bg-brand-700 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
              All ({violations.length})
            </button>
            {VIOLATION_TYPES.filter(t => violations.some(v => v.type === t.value)).map(t => (
              <button key={t.value} onClick={() => setTypeFilter(t.value)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap ${
                  typeFilter === t.value ? 'bg-brand-700 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                {t.label.split(' ').slice(1).join(' ')} ({violations.filter(v => v.type === t.value).length})
              </button>
            ))}
          </div>
        )}

        {/* Timeline */}
        {grouped.length === 0 ? (
          <div className="card bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 text-center py-12 flex flex-col items-center gap-3 mt-2">
            <span className="text-5xl">📋</span>
            <p className="font-bold text-slate-800 dark:text-slate-100">No violations logged</p>
            <p className="text-xs text-slate-500 max-w-xs">
              Document every order violation the moment it happens. Date, time, and specifics are critical for contempt proceedings.
            </p>
            <Button variant="primary" size="md" onClick={() => setShowForm(true)}>
              Log First Violation
            </Button>
          </div>
        ) : (
          grouped.map(([date, items]) => (
            <div key={date}>
              {/* Date header */}
              <div className="flex items-center gap-2 mb-3 sticky top-0 bg-white dark:bg-slate-900 py-1 z-10">
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {fmtDate(date)}
                </p>
                <div className="flex-1 h-px bg-slate-100 dark:bg-slate-700" />
                <p className="text-xs text-slate-400">{date}</p>
              </div>
              <div className="flex flex-col gap-3 mb-4">
                {items.map(v => (
                  <ViolationCard
                    key={v.id}
                    violation={v}
                    onDelete={deleteViolation}
                    onEdit={setEditTarget}
                    onFileMotion={handleFileMotion}
                  />
                ))}
              </div>
            </div>
          ))
        )}

        {/* Legal disclaimer */}
        {violations.length > 0 && (
          <div className="disclaimer mt-2">
            ⚠️ These records are for documentation purposes. Consult an attorney before filing any court motion. Ensure all facts are accurate and verifiable.
          </div>
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => setShowForm(true)}
        className="fixed bottom-20 right-4 w-14 h-14 bg-red-600 hover:bg-red-700 rounded-full shadow-lg flex items-center justify-center text-white z-30 active:scale-95 transition-transform"
        aria-label="Log violation">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </button>

      {showForm && (
        <AddViolationModal
          onClose={() => setShowForm(false)}
          onSave={addViolation}
          orders={orders}
        />
      )}

      {editTarget && (
        <EditViolationModal
          violation={editTarget}
          onClose={() => setEditTarget(null)}
          onSave={updateViolation}
          orders={orders}
        />
      )}
    </div>
  )
}
